/** Otomatik forum açıklaması — çoklu şablon, kategori odaklı, istatistikli metinler */

export const DESCRIPTION_VARIANT_COUNT = 12;

export type ForumDescriptionInput = {
  forum_id: string;
  name: string;
  category?: string | null;
  members_count?: number | null;
  posts_count?: number | null;
  topics_count?: number | null;
};

export type DescriptionContext = {
  name: string;
  categoryLabel: string;
  members: string;
  posts: string;
  topics: string;
};

function hashString(value: string): number {
  let h = 0;
  for (let i = 0; i < value.length; i++) {
    h = (Math.imul(31, h) + value.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function pickIndex(forumId: string, salt: string, modulo: number): number {
  return hashString(`${forumId.toLowerCase()}:${salt}`) % modulo;
}

function fmtStat(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString('tr-TR');
}

function buildContext(forum: ForumDescriptionInput): DescriptionContext {
  return {
    name: forum.name,
    categoryLabel: forum.category?.trim() || 'Genel',
    members: fmtStat(forum.members_count),
    posts: fmtStat(forum.posts_count),
    topics: fmtStat(forum.topics_count),
  };
}

function lowerFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/** Kategori odak paragrafları — her biri tam cümle */
const CATEGORY_THEMES: Record<string, string[]> = {
  Teknoloji: [
    'Donanım toplama, ekran kartı ve işlemci seçiminde rehber niteliğinde tartışmalar sunar; güncel teknoloji haberleri ve incelemeler ön plandadır.',
    'Yazılım güncellemeleri, işletim sistemi ipuçları, siber güvenlik ve açık kaynak projeler hakkında deneyim paylaşımına ayrılmış bir yapıya sahiptir.',
    'Mobil cihazlar, akıllı telefonlar ve tablet ekosisteminde kullanıcı yorumları ile teknik destek içerikleri bir arada bulunur.',
    'Oyun teknolojileri, performans optimizasyonu ve donanım uyumluluğu konularında derinlemesine kullanıcı geri bildirimleri yer alır.',
    'Yapay zeka, bulut hizmetleri ve geliştirici araçları gibi geleceğe dönük teknoloji başlıklarında aktif sohbet kanalları barındırır.',
    'İnternet altyapısı, modem-router kurulumu ve ev ağı sorunlarına yönelik pratik çözüm önerileri paylaşılır.',
    'Ürün karşılaştırmaları, fiyat-performans analizleri ve satın alma öncesi kullanıcı deneyimleri detaylı biçimde tartışılır.',
    'Teknik arıza giderme, garanti süreçleri ve servis deneyimleri hakkında topluluk destekli bilgi bankası oluşturulur.',
    'Yazılım geliştirme, web teknolojileri ve dijital üretkenlik araçları üzerine kaynak ve öneri paylaşımı yapılır.',
    'Tüketici elektroniği, akıllı ev sistemleri ve giyilebilir teknoloji ürünlerine dair güncel değerlendirmeler sunulur.',
    'Teknoloji politikaları, dijital haklar ve sektör haberleri çerçevesinde bilinçli tartışma ortamı sağlar.',
    'Yeni çıkan ürün lansmanları, kampanyalar ve teknoloji fuarları hakkında canlı yorum akışı bulunur.',
  ],
  Oyun: [
    'PC, konsol ve mobil platformlarda oyun incelemeleri, performans ayarları ve sistem gereksinimleri tartışılır.',
    'MMORPG, rekabetçi çok oyunculu ve kooperatif oyunlarda rehber, build ve strateji paylaşımı yoğundur.',
    'Oyun güncellemeleri, yama notları ve meta değişiklikleri hakkında hızlı geri bildirim topluluğu oluşturur.',
    'İndirim dönemleri, mağaza fırsatları ve ücretsiz oyun duyuruları takip eden oyunculara hitap eder.',
    'E-spor haberleri, turnuva sonuçları ve profesyonel sahne analizleri düzenli paylaşılır.',
    'Modlama, topluluk yapımı içerikler ve sunucu önerileri için teknik destek başlıkları bulunur.',
    'Hikâye odaklı, bağımsız ve retro oyunlar üzerine nostalji ve keşif temalı sohbetler sürdürülür.',
    'Donanım-performans ilişkisi, FPS optimizasyonu ve streaming kurulumu konularında rehber içerik üretilir.',
    'Guild, klan ve oyuncu grubu organizasyonları için duyuru ve eşleştirme paylaşımları yapılır.',
    'Oyun tasarımı, geliştirme süreçleri ve sektör haberleri meraklılarına yönelik tartışma alanları sunar.',
    'Yeni çıkan yapımlar hakkında spoiler kontrollü inceleme ve kullanıcı puanlamaları yer alır.',
    'Türkçe topluluk etkinlikleri, turnuvalar ve oyun gecesi organizasyonlarına dair duyurular paylaşılır.',
  ],
  Spor: [
    'Futbol maç analizleri, kadro yorumları ve transfer spekülasyonları gündemin merkezinde tutulur.',
    'Basketbol, voleybol ve diğer branşlarda istatistik temelli tartışmalar ve maç önizlemeleri sunulur.',
    'Türk takımları ve milli maçlar özelinde taraftar yorumları ile teknik analizler bir arada yer alır.',
    'Spor tarihi, efsane futbolcular ve nostalji başlıklarında arşiv niteliğinde paylaşımlar yapılır.',
    'Fantezi lig, bahis stratejisi ve tahmin kültürü hakkında bilgilendirici sohbetler bulunur.',
    'Fitness, antrenman programları ve amatör spor deneyimleri spor gündeminin tamamlayıcısıdır.',
    'Olimpiyat, dünya kupası ve uluslararası turnuva takvimleri yakından izlenir.',
    'Hakem kararları, VAR tartışmaları ve lig düzenlemeleri hakkında canlı yorum akışı oluşturulur.',
    'Genç yetenekler, altyapı haberleri ve kulüp ekonomisi konularında derinlemesine içerik üretilir.',
    'Motorspor, tenis ve bireysel branşlarda sonuç ve performans değerlendirmeleri paylaşılır.',
    'Spor medyası, yayın hakları ve dijital platform haberleri takip eden kullanıcı kitlesine uygundur.',
    'Taraftar grupları, deplasman deneyimleri ve stadyum kültürü üzerine topluluk hikâyeleri anlatılır.',
  ],
  Eğitim: [
    'Üniversite sınavı, KPSS ve kamu personeli hazırlığında kaynak önerileri ve deneme analizleri paylaşılır.',
    'Lise müfredatı, ders çalışma teknikleri ve ödev yardımı başlıklarında öğrenci dayanışması ön plandadır.',
    'Yükseköğretim programları, bölüm seçimi ve kampüs yaşamı hakkında deneyim aktarımı yapılır.',
    'Yabancı dil öğrenimi, sertifika sınavları ve kaynak tavsiyeleri için düzenli rehber içerikler bulunur.',
    'Meslek gelişimi, staj fırsatları ve iş görüşmesi deneyimleri kariyer odaklı tartışmalara konu olur.',
    'Öğretmen adayları, eğitim politikaları ve okul sistemine dair güncel değerlendirmeler sunulur.',
    'Açık öğretim, uzaktan eğitim ve dijital öğrenme platformları hakkında kullanıcı yorumları yer alır.',
    'Akademik araştırma, tez süreci ve bilimsel yazım konularında destekleyici paylaşımlar yapılır.',
    'Burs, kredi ve öğrenci hakları hakkında bilgilendirici başlıklar topluluk tarafından güncellenir.',
    'Kurs önerileri, online eğitim incelemeleri ve sertifikasyon programları karşılaştırmalı tartışılır.',
    'Eğitim teknolojileri, akıllı tahta ve dijital sınıf uygulamaları hakkında saha deneyimleri paylaşılır.',
    'Öğrenci kulüpleri, proje yarışmaları ve bilim olimpiyatlarına dair duyuru ve rehberler bulunur.',
  ],
  Sağlık: [
    'Genel sağlık bilinci, koruyucu hekimlik ve yaşam tarzı önerileri bilgilendirici çerçevede paylaşılır.',
    'Beslenme düzeni, kilo yönetimi ve sporla sağlık ilişkisi hakkında deneyim temelli tartışmalar yapılır.',
    'Kronik hastalıklar, ilaç kullanımı ve tedavi süreçlerinde topluluk desteği ve bilgi alışverişi sunulur.',
    'Ruh sağlığı, stres yönetimi ve psikolojik iyi oluş konularında hassas ve saygılı sohbet ortamı hedeflenir.',
    'Anne-bebek sağlığı, hamilelik süreci ve çocuk gelişimi hakkında pratik öneriler paylaşılır.',
    'Alternatif tıp, bitkisel ürünler ve tamamlayıcı yaklaşımlar hakkında kullanıcı deneyimleri tartışılır.',
    'Hastane süreçleri, sağlık sigortası ve hasta hakları hakkında yönlendirici bilgi başlıkları bulunur.',
    'Spor yaralanmaları, rehabilitasyon ve fizik tedavi konularında motivasyon odaklı paylaşımlar yapılır.',
    'Sağlıklı tarifler, diyet programları ve kalori hesaplama araçları hakkında öneriler sunulur.',
    'Aşı, salgın hastalıklar ve kamu sağlığı duyuruları güncel bilgi akışıyla takip edilir.',
    'Diş sağlığı, göz sağlığı ve uzmanlık alanlarına dair soru-cevap formatında destek verilir.',
    'Sağlık turizmi, check-up ve check-up sonuçları hakkında deneyim paylaşımı yapılır.',
  ],
  Otomotiv: [
    'Sıfır ve ikinci el araç alım-satımında fiyat analizi, ekspertiz ve pazarlık deneyimleri paylaşılır.',
    'Periyodik bakım, yağ değişimi ve arıza tespiti konularında uygulamalı rehberler sunulur.',
    'Modifikasyon, performans yükseltme ve estetik aksesuar tartışmaları tutkunlara yöneliktir.',
    'Elektrikli araçlar, hibrit teknolojiler ve şarj altyapısı hakkında güncel değerlendirmeler yapılır.',
    'Klasik otomobiller, restorasyon projeleri ve koleksiyon kültürü hakkında arşiv paylaşımları bulunur.',
    'Lastik, jant, süspansiyon ve fren sistemleri üzerine teknik karşılaştırmalar tartışılır.',
    'Trafik sigortası, kasko ve yasal süreçler hakkında bilgilendirici başlıklar yer alır.',
    'Motorspor, ralli ve pist deneyimleri hakkında heyecan odaklı topluluk içerikleri üretilir.',
    'Ticari araçlar, lojistik ve filo yönetimi konularında profesyonel kullanıcı yorumları paylaşılır.',
    'Yakıt tüketimi, uzun yol deneyimleri ve konfor karşılaştırmaları detaylı biçimde ele alınır.',
    'Otomotiv sektör haberleri, yeni model lansmanları ve fuar duyuruları takip edilir.',
    'Üretici kampanyaları, servis anlaşmaları ve garanti kapsamı hakkında kullanıcı geri bildirimleri sunulur.',
  ],
  Hobi: [
    'El işi, DIY projeler ve evde üretim fikirleri adım adım rehberlerle paylaşılır.',
    'Fotoğrafçılık, ekipman incelemeleri ve kompozisyon teknikleri hakkında yaratıcı tartışmalar yapılır.',
    'Müzik enstrümanları, kayıt ekipmanları ve beste süreçleri meraklılarına hitap eder.',
    'Koleksiyon hobileri, nadir parçalar ve değer biçimi konularında uzmanlaşmış sohbetler bulunur.',
    'Bahçecilik, bitki bakımı ve peyzaj düzenleme önerileri mevsimsel içeriklerle güncellenir.',
    'Model yapım, radyo kontrollü araçlar ve maker projeleri teknik detaylarla anlatılır.',
    'Astronomi, doğa gözlemi ve bilim meraklılarına yönelik keşif paylaşımları yapılır.',
    'Yemek pişirme, pastacılık ve mutfak ekipmanı incelemeleri lezzet odaklı tartışmalara konu olur.',
    'Balıkçılık, avcılık ve outdoor aktiviteler hakkında saha deneyimleri aktarılır.',
    'Grafik tasarım, 3D baskı ve dijital hobi araçları üzerine kaynak önerileri sunulur.',
    'Evcil hayvan bakımı, eğitim ve sağlık konularında sorumlu sahiplik rehberleri paylaşılır.',
    'Sanat atölyesi, boyama teknikleri ve malzeme seçimi hakkında ilham verici içerikler üretilir.',
  ],
  Finans: [
    'Borsa İstanbul, hisse analizleri ve teknik grafik yorumları yatırımcı odaklı tartışılır.',
    'Döviz kurları, altın ve emtia piyasaları hakkında günlük piyasa değerlendirmeleri paylaşılır.',
    'Kişisel bütçe yönetimi, tasarruf stratejileri ve borç kontrolü konularında pratik öneriler sunulur.',
    'Kripto para, blockchain ve dijital varlık ekosisteminde risk bilinciyle bilgi alışverişi yapılır.',
    'Gayrimenkul yatırımı, kira getirisi ve bölgesel piyasa trendleri detaylı biçimde ele alınır.',
    'Emeklilik planlaması, BES ve uzun vadeli birikim araçları hakkında karşılaştırmalar yapılır.',
    'Vergi mevzuatı, beyan süreçleri ve mali danışmanlık deneyimleri paylaşılır.',
    'Girişimcilik, KOBİ finansmanı ve iş kurma maliyetleri hakkında rehber içerikler bulunur.',
    'Ekonomi politikaları, enflasyon ve faiz kararları üzerine makro analizler tartışılır.',
    'Bankacılık ürünleri, kredi kartları ve kampanya karşılaştırmaları tüketici perspektifiyle sunulur.',
    'Uluslararası piyasalar, fon yönetimi ve portföy çeşitlendirme stratejileri ele alınır.',
    'Finansal okuryazarlık, dolandırıcılık uyarıları ve güvenli yatırım ilkeleri vurgulanır.',
  ],
  Yaşam: [
    'Ev düzeni, depolama çözümleri ve minimal yaşam tarzı hakkında ilham verici paylaşımlar yapılır.',
    'Ebeveynlik, çocuk gelişimi ve aile ilişkileri üzerine deneyim temelli sohbetler sürdürülür.',
    'Kişisel gelişim, verimlilik ve alışkanlık oluşturma teknikleri tartışma konusudur.',
    'Moda, stil önerileri ve gardırop planlaması hakkında pratik kullanıcı yorumları bulunur.',
    'İlişkiler, iletişim becerileri ve sosyal yaşam hakkında saygılı paylaşım ortamı sağlanır.',
    'Ev dekorasyonu, mobilya seçimi ve renk uyumu konularında görsel öneriler paylaşılır.',
    'Evcil hayvan sahipliği, bakım rutinleri ve ürün tavsiyeleri günlük yaşamın parçasıdır.',
    'Sağlıklı yaşam rutinleri, uyku düzeni ve stres azaltma yöntemleri ele alınır.',
    'Düğün, nişan ve özel gün organizasyonu hakkında bütçe dostu rehberler sunulur.',
    'Komşuluk, apartman yaşamı ve yerel topluluk ilişkileri hakkında deneyimler aktarılır.',
    'Hobi-iş dengesi, freelance yaşam ve uzaktan çalışma ipuçları paylaşılır.',
    'Gönüllülük, sosyal sorumluluk ve toplumsal dayanışma projelerine dair duyurular yer alır.',
  ],
  Sanat: [
    'Resim, illüstrasyon ve dijital sanat teknikleri hakkında yapıcı eleştiri ve örnek paylaşımı yapılır.',
    'Edebiyat, şiir ve yaratıcı yazarlık çalışmaları okuyucu geri bildirimiyle geliştirilir.',
    'Sinema, dizi ve belgesel incelemeleri spoiler kurallarına uygun biçimde tartışılır.',
    'Tiyatro, opera ve sahne sanatları etkinlikleri hakkında güncel duyurular paylaşılır.',
    'Müzik albümü, konser ve festival değerlendirmeleri geniş bir yelpazede sunulur.',
    'Fotoğraf sanatı, sergi haberleri ve sanatçı portföyleri toplulukla buluşturulur.',
    'Mimari, tasarım tarihi ve görsel kültür üzerine akademik meraklı içerikler üretilir.',
    'Kültürel miras, müze ziyaretleri ve tarihî eserler hakkında bilgilendirici paylaşımlar yapılır.',
    'Grafik roman, anime ve popüler kültür üretimleri hakkında hayran tartışmaları sürdürülür.',
    'Sanat eğitimi, atölye önerileri ve malzeme rehberleri yeni başlayanlara yöneliktir.',
    'Yaratıcı ekonomi, freelance sanatçılık ve projelerin finansmanı konuları ele alınır.',
    'Uluslararası sanat haberleri, bienal ve festival takvimi yakından izlenir.',
  ],
  Seyahat: [
    'Yurt içi rota önerileri, konaklama incelemeleri ve bütçe dostu gezi planları paylaşılır.',
    'Yurt dışı vize süreçleri, uçuş ipuçları ve bagaj hazırlığı hakkında güncel rehberler sunulur.',
    'Kamp, trekking ve doğa yürüyüşü deneyimleri outdoor tutkunlarına hitap eder.',
    'Kültür turları, müze gezileri ve gastronomi rotaları detaylı anlatımlarla tartışılır.',
    'Aile tatilleri, çocuklu seyahat ve güvenli destinasyon önerileri paylaşılır.',
    'Solo seyahat, sırt çantası gezginleri ve yerel ulaşım hackleri hakkında ipuçları bulunur.',
    'Otel, pansiyon ve kısa süreli kiralama deneyimleri karşılaştırmalı değerlendirilir.',
    'Fotoğraf rotaları, drone çekimi kuralları ve seyahat ekipmanı önerileri ele alınır.',
    'Festivaller, etkinlik takvimi ve sezonluk gezi fırsatları duyurulur.',
    'Deniz tatili, kış turizmi ve termal kaplıca önerileri bölgesel bazda tartışılır.',
    'Seyahat sigortası, sağlık önlemleri ve acil durum planlaması hakkında bilgi paylaşılır.',
    'Gezgin blogları, rota haritaları ve topluluk organizasyonlu turlar tanıtılır.',
  ],
  Alışveriş: [
    'Ürün incelemeleri, uzun süreli kullanım deneyimleri ve dayanıklılık testleri paylaşılır.',
    'Fiyat karşılaştırması, indirim takibi ve kampanya duyuruları tüketici odaklı sunulur.',
    'Teknoloji mağazaları, beyaz eşya ve küçük ev aletleri hakkında satın alma rehberleri bulunur.',
    'Online alışveriş güvenliği, iade süreçleri ve müşteri hizmetleri deneyimleri tartışılır.',
    'Moda, ayakkabı ve aksesuar trendleri bütçe dostu alternatiflerle birlikte ele alınır.',
    'Market alışverişi, gıda ürünleri ve fiyat-performans değerlendirmeleri güncellenir.',
    'Kupon, cashback ve sadakat programları hakkında tasarruf ipuçları paylaşılır.',
    'İkinci el platformlar, takas kültürü ve sürdürülebilir tüketim önerileri vurgulanır.',
    'Çocuk ürünleri, bebek malzemeleri ve ebeveyn tavsiyeleri güvenilir kaynak arayanlara uygundur.',
    'Ev tekstili, mobilya ve dekorasyon alışverişinde kullanıcı fotoğraflı yorumlar yer alır.',
    'Sahte ürün uyarıları, garanti belgeleri ve yetkili satıcı listeleri toplulukça takip edilir.',
    'Kurumsal alışveriş, toplu sipariş ve KOBİ tedarik deneyimleri profesyonel perspektifle paylaşılır.',
  ],
  Sosyal: [
    'Gündelik sohbet, güncel olaylar ve toplumsal gündem hakkında özgür tartışma ortamı sunar.',
    'Mizah, meme kültürü ve eğlence içerikleriyle hafif tempolu paylaşımlar öne çıkar.',
    'İnternet fenomenleri, sosyal medya trendleri ve dijital kültür başlıkları takip edilir.',
    'Topluluk anketleri, oylamalar ve kolektif karar alma etkinlikleri düzenlenir.',
    'Gece sohbetleri, introvert-extrovert deneyimleri ve sosyal kaygı konuları paylaşılır.',
    'Arkadaşlık, tanışma ve ortak ilgi grupları için güvenli iletişim alanları oluşturulur.',
    'Televizyon, magazin ve popüler kültür haberleri hızlı yorum döngüsüyle tartışılır.',
    'Oyun gecesi, online buluşma ve sanal etkinlik duyuruları topluluk tarafından paylaşılır.',
    'Nostalji başlıkları, 90lar-2000ler kültürü ve eski internet anıları canlandırılır.',
    'Sivil toplum, gönüllülük ve yerel yardım kampanyalarına dair duyurular yayınlanır.',
    'Gençlik kültürü, müzik zevkleri ve kuşaklar arası mizah karşılaştırmaları yapılır.',
    'Forum adabı, moderasyon deneyimleri ve topluluk kuralları hakkında rehberler bulunur.',
  ],
  Bölgesel: [
    'Şehir ve ilçe gündemi, yerel haberler ve mahalle gelişmeleri yakından takip edilir.',
    'Yerel esnaf, işletme önerileri ve hizmet kalitesi hakkında kullanıcı yorumları paylaşılır.',
    'Ulaşım, metro hatları ve şehir içi ulaşım sorunları hakkında güncel bilgiler sunulur.',
    'Belediye duyuruları, altyapı çalışmaları ve kamu hizmetleri deneyimleri tartışılır.',
    'Yerel festivaller, konserler ve kültürel etkinlik takvimi toplulukla güncellenir.',
    'Okul, hastane ve kamu kurumu hizmetleri hakkında bölgesel geri bildirimler yer alır.',
    'Emlak, kira trendleri ve mahalle yaşam kalitesi karşılaştırmalı ele alınır.',
    'Yerel tarih, kültürel miras ve şehir rehberi içerikleri arşiv niteliğinde tutulur.',
    'Deprem, sel ve acil durum hazırlığı hakkında dayanışma odaklı paylaşımlar yapılır.',
    'Yerel spor kulüpleri, amatör ligler ve taraftar organizasyonları desteklenir.',
    'Göç, nüfus hareketleri ve bölgesel ekonomi hakkında veri destekli yorumlar sunulur.',
    'Komşuluk ilişkileri, site yönetimi ve apartman sorunları için çözüm önerileri paylaşılır.',
  ],
};

const STAT_BLOCKS: Array<(c: DescriptionContext) => string> = [
  (c) =>
    `Toplulukta ${c.members} kayıtlı üye bulunuyor; ${c.topics} konu başlığı ve ${c.posts} iletiyle süregelen bir paylaşım trafiği oluşturuyor.`,
  (c) =>
    `${c.members} üyeli yapısı, ${c.topics} açılmış konu ve ${c.posts} iletiyle Türkiye forum ekosisteminde ölçülebilir bir aktivite profili çiziyor.`,
  (c) =>
    `İstatistiklere göre ${c.members} üye, ${c.posts} ileti ve ${c.topics} konu; hem yeni başlıklar hem de arşiv niteliğindeki tartışmalar için zengin bir taban sunuyor.`,
  (c) =>
    `Kayıtlı ${c.members} kullanıcı kitlesi, ${c.topics} konu ve ${c.posts} iletiyle düzenli güncellenen bir içerik akışına sahip.`,
  (c) =>
    `${c.topics} konu ve ${c.posts} ileti sayısı, ${c.members} üyelik tabanıyla birlikte forumun uzun soluklu tartışma geleneğini yansıtıyor.`,
  (c) =>
    `Üye sayısı ${c.members} seviyesinde; ${c.posts} ileti hacmi ve ${c.topics} konu çeşitliliği site içi karşılaştırmalarda referans alınabilir.`,
  (c) =>
    `${c.name} topluluğu ${c.members} üye ile ölçülürken, ${c.topics} konu başlığı altında ${c.posts} ileti birikmiş durumda.`,
  (c) =>
    `Aktif üye tabanı ${c.members} civarında; ${c.posts} ileti ve ${c.topics} konu ile hem güncel hem geçmiş içeriklere erişim mümkün.`,
  (c) =>
    `${c.members} üye · ${c.topics} konu · ${c.posts} ileti üçlüsü, forumun Kitapçık üzerindeki büyüme ve aktivite görünümünü belirliyor.`,
  (c) =>
    `Toplam ${c.posts} ileti ve ${c.topics} konu, ${c.members} kayıtlı kullanıcıyla desteklenen canlı bir tartışma ortamına işaret ediyor.`,
  (c) =>
    `${c.members} üyelik ölçeğinde bir topluluk; ${c.topics} konu kategorizasyonu ve ${c.posts} ileti yoğunluğuyla dikkat çekiyor.`,
  (c) =>
    `Forum verileri: ${c.members} üye, ${c.topics} konu, ${c.posts} ileti — üye başına düşen içerik hacmi karşılaştırmalı incelenebilir.`,
];

const CLOSING_LINES: string[] = [
  'Kitapçık üzerinden üye, konu ve ileti sayıları düzenli takip edilebilir.',
  'Forum istatistikleri ve sıralaması Kitapçık listesinde güncel tutulur.',
  'Türkiye forum rehberi Kitapçık’ta benzer sitelerle kıyaslanabilir.',
  'Aktivite trendleri ve büyüme göstergeleri Kitapçık veri panelinde yer alır.',
  'Yeni ziyaretçiler için forum özeti ve kategori konumu hızlıca görülebilir.',
  'Topluluk dinamikleri, üye artışı ve ileti trafiği periyodik olarak ölçülür.',
  'Benzer kategorideki diğer forumlarla istatistik karşılaştırması yapılabilir.',
  'Siteye eklenme tarihi ve son güncelleme bilgisi Kitapçık kayıtlarında bulunur.',
  'Forum puanı, büyüme yüzdesi ve lig sıralaması Kitapçık metrikleriyle sunulur.',
  'Arşiv derinliği ve güncel tartışma dengesi Kitapçık analizlerinde öne çıkar.',
  'Ziyaretçiler konu başlıklarına ve üye profiline forum detay sayfasından ulaşabilir.',
  'Liste dışı kalan forumlar için öneri formu üzerinden topluluk desteği sağlanır.',
];

const GENERAL_INTROS: Array<(c: DescriptionContext) => string> = [
  (c) =>
    `${c.name}, ${c.categoryLabel} alanında faaliyet gösteren ve Türkiye’deki forum ziyaretçilerine hitap eden köklü bir tartışma platformudur.`,
  (c) =>
    `Türkiye forum ekosisteminde ${c.name} adıyla anılan site, ${c.categoryLabel} kategorisinde geniş kapsamlı kullanıcı etkileşimi sunar.`,
  (c) =>
    `${c.name} forum sitesi; ${c.categoryLabel} başlığı altında toplanan, deneyim paylaşımına ve güncel gündeme açık bir topluluktur.`,
  (c) =>
    `Kitapçık veritabanında kayıtlı ${c.name}, ${c.categoryLabel} segmentindeki forumlar arasında istatistikleriyle takip edilen bir platformdur.`,
  (c) =>
    `${c.categoryLabel} meraklılarının buluştuğu ${c.name}, uzun yıllara dayanan kullanıcı katkılarıyla zenginleşen bir içerik arşivi barındırır.`,
  (c) =>
    `${c.name} — ${c.categoryLabel} odaklı forum yapısıyla hem yeni üyelere hem deneyimli kullanıcılara açık tartışma kanalları sunar.`,
  (c) =>
    `Forum dünyasında ${c.name} markası, ${c.categoryLabel} konusunda Türkçe içerik üreten aktif topluluklardan biridir.`,
  (c) =>
    `${c.name} topluluğu, ${c.categoryLabel} alanındaki sorulara yanıt arayan ve deneyim aktaran kullanıcıların bir araya geldiği bir platformdur.`,
  (c) =>
    `Çevrimiçi tartışma kültüründe ${c.name}, ${c.categoryLabel} gündemini takip eden geniş bir üye kitlesine ev sahipliği yapar.`,
  (c) =>
    `${c.name} sitesi; moderasyon, kategori yapısı ve kullanıcı katkılarıyla ${c.categoryLabel} forumları arasında öne çıkan adreslerdendir.`,
  (c) =>
    `Türkiye’de ${c.categoryLabel} forumu arayanlar için ${c.name}, güncel konu başlıkları ve arşiv derinliğiyle referans niteliği taşır.`,
  (c) =>
    `${c.name} platformu, ${c.categoryLabel} segmentindeki haber, rehber ve kullanıcı yorumlarını tek çatı altında toplar.`,
];

const CATEGORY_INTROS: Array<(c: DescriptionContext, theme: string) => string> = [
  (c, theme) =>
    `${c.name}, ${c.categoryLabel} kategorisinde konumlanan ve ${lowerFirst(theme)}`,
  (c, theme) =>
    `Türkiye’nin tanınan ${c.categoryLabel} forumlarından ${c.name}; ${lowerFirst(theme)}`,
  (c, theme) =>
    `${c.name} — ${c.categoryLabel} alanında: ${lowerFirst(theme)}`,
  (c, theme) =>
    `Kitapçık’ta listelenen ${c.name} (${c.categoryLabel}), ${lowerFirst(theme)}`,
  (c, theme) =>
    `${c.categoryLabel} forumları içinde öne çıkan ${c.name} sitesi ${lowerFirst(theme)}`,
  (c, theme) =>
    `${c.name} topluluğu, ${c.categoryLabel} segmentinde ${lowerFirst(theme)}`,
  (c, theme) =>
    `Forum rehberi Kitapçık’ta yer alan ${c.name}, ${c.categoryLabel} başlığında ${lowerFirst(theme)}`,
  (c, theme) =>
    `${c.name} markasıyla bilinen ${c.categoryLabel} platformu; ${lowerFirst(theme)}`,
  (c, theme) =>
    `Aktif Türkçe ${c.categoryLabel} topluluğu ${c.name}: ${lowerFirst(theme)}`,
  (c, theme) =>
    `${c.name} forumu, ${c.categoryLabel} meraklıları için ${lowerFirst(theme)}`,
  (c, theme) =>
    `Çevrimiçi ${c.categoryLabel} tartışmalarında ${c.name} adıyla anılan site ${lowerFirst(theme)}`,
  (c, theme) =>
    `${c.name}, ${c.categoryLabel} odaklı içerik üretiminde ${lowerFirst(theme)}`,
];

export function generateAutoDescription(forum: ForumDescriptionInput): string {
  const ctx = buildContext(forum);
  const variant = pickIndex(forum.forum_id, 'variant', DESCRIPTION_VARIANT_COUNT);
  const statBlock = STAT_BLOCKS[pickIndex(forum.forum_id, 'stats', STAT_BLOCKS.length)](ctx);
  const closing = CLOSING_LINES[pickIndex(forum.forum_id, 'closing', CLOSING_LINES.length)];

  const categoryKey = forum.category?.trim();
  const themes = categoryKey ? CATEGORY_THEMES[categoryKey] : undefined;

  if (themes?.length) {
    const themeIndex = pickIndex(forum.forum_id, 'theme', themes.length);
    const introIndex = pickIndex(forum.forum_id, 'intro', CATEGORY_INTROS.length);
    const theme = themes[themeIndex % themes.length];
    const intro = CATEGORY_INTROS[introIndex % CATEGORY_INTROS.length](ctx, theme);
    return `${intro} ${statBlock} ${closing}`;
  }

  const intro = GENERAL_INTROS[variant % GENERAL_INTROS.length](ctx);
  return `${intro} ${statBlock} ${closing}`;
}
